import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  saveErrorReason,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { useWebhookSubscriptionsList } from "../api/useWebhookSubscriptionsList";
import { useWebhookSubscriptionMutations } from "../api/useWebhookSubscriptionMutations";
import { eventTypeLabel } from "../types";
import type { WebhookSubscription } from "../types";
import { buildWebhookBatchActions } from "./batchActions";

const PAGE_SIZE = 25;

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const sel = useBatchSelection([]);
  const { destroy } = useWebhookSubscriptionMutations();
  const { rows, pageMeta, isFetching, refetch } = useWebhookSubscriptionsList({
    page: { number: pageNumber, size: pageSize },
  });

  const columns: ColumnDef<WebhookSubscription>[] = [
    {
      accessorKey: "eventType",
      header: "Event",
      cell: ({ row }) => eventTypeLabel(row.original.eventType),
    },
    {
      accessorKey: "webhookUrl",
      header: "Webhook URL",
      cell: ({ row }) => row.original.webhookUrl,
    },
  ];

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    const targets = [...sel.selectedIds];
    const failures: BatchFailure[] = [];
    for (const id of targets) {
      try {
        await destroy(String(id));
      } catch (err) {
        failures.push({ reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? {
            variant: "success",
            text: `${targets.length} webhook subscription(s) deleted.`,
          }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed on ${failures.length} of ${targets.length} subscription(s).`,
              failures
            ),
          }
    );
    sel.clear();
    refetch();
  };

  return (
    <>
      <AdminListCard
        title="Webhooks"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Webhook Subscription"
        batchActions={buildWebhookBatchActions(() => setConfirmOpen(true))}
        selectedCount={sel.selectedIds.length}
      >
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No webhook subscriptions found."
          paginationEnabled
          pageSizeEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={(p) => {
            sel.clear();
            setPageNumber(p);
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            sel.clear();
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title="Delete webhook subscriptions?"
        message={`Are you sure you want to delete ${sel.selectedIds.length} webhook subscription(s)?`}
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
